'use client'

import { Check, Mail } from 'lucide-react'
import Link from 'next/link'
import { useMemo, useState } from 'react'

import { GlowFrame } from '@/components/atoms/glow-frame'
import { TILT_GROUP_ITEM, TiltGroup } from '@/components/atoms/tilt-group.client'
import { PageHeader } from '@/components/molecules/page-header'
import { RestockAlertForm } from '@/components/molecules/restock-alert.client'
import { TierCard } from '@/components/molecules/tier-card.client'
import { TiltCard } from '@/components/molecules/tilt-card.client'
import { ThumbnailGalleryBand } from '@/components/sections/thumbnail-gallery-band'
import { CONTAINER, SCREEN_TOP } from '@/lib/layout'
import { quoteIcon, quoteTone } from '@/lib/offer-visuals'
import { type QuoteOptionView, type ServiceView, sellsPackages } from '@/lib/services'
import { CTA_BLOOM, PICKED_FILL, PICKED_RING, RADIUS } from '@/lib/surface'
import { OFFER_SCREEN } from '@/lib/tier-fit'
import { ACCENT_TEXT, BODY, KICKER, MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * Graphics offer: priced tiers on one side, quote-only work on the other.
 *
 * Both halves come from the database (`service.href = '/pl/grafika'`), and they
 * are now sold by TWO routes: `/pl/miniaturki` takes the packages, `/pl/grafika`
 * takes the work that is quoted by enquiry. `sells` says which half a given
 * render is for, so neither page has to know what the other holds.
 *
 * The tab shell survives that split, because it still earns its keep: file a
 * second packaged service under this page in the panel and `/pl/miniaturki`
 * grows a tab for it with no edit here.
 *
 * ## What went away
 *
 * A third tab used to sit between them — the `grafika` offering's own Starter /
 * Pro / Brand ladder. It is out of the offer by request, and as of 2026-09-19
 * out of the CATALOGUE too: *„nie ma poziomów i cen dla grafiki, są tylko
 * miniaturki"*. Hiding it from this page had left it on sale in the creator at
 * 120 zł, under a page that says graphics are quoted one by one. The offering
 * ROW stays regardless: the quote options hang off that same row, so deleting
 * it would empty `/pl/grafika` rather than tidy it.
 *
 * The two halves never share a checkout. Quote-only work has no rate card, so
 * its panel collects what the client needs and sends it to the enquiry — it
 * shows no total and no cart button, because any number there would be
 * invented. Priced work hands off to `/pl/kreator`, which is now the one place
 * on the site that sizes an order and the one that puts it in the basket; the
 * near-identical configurator this page used to carry is gone with it.
 *
 * ## The rebuild
 *
 * The priced half no longer draws its own tier card — `TierCard` does, the same
 * one `/pl/montaz` uses, which is what stops these routes from
 * drifting apart one fix at a time.
 *
 * The quote half was nine grey boxes, every one of them ending in the same
 * uppercase "WYCENA MAILOWA" under a notice that had already said it once. The
 * label is gone and each option has its own glyph and hue instead, so the grid
 * is nine recognisable things rather than nine paragraphs.
 */

/** Id of the synthetic last tab; no service can collide (slugs have no dots). */
const QUOTE_TAB = 'do-wyceny.'

export function GraphicsPageView({
  locale,
  services,
  sells,
}: {
  locale: string
  services: ServiceView[]
  /** Which half of the catalogue this route sells. */
  sells: 'packages' | 'quotes'
}) {
  // Filtered here rather than by the routes, so "a package is a service with
  // plans" and "an option is priced by enquiry" stay one definition each.
  const priced = sells === 'packages' ? services.filter(sellsPackages) : []
  const quoteOptions = sells === 'quotes' ? services.flatMap((s) => s.quoteOptions) : []

  const tabs = [
    ...priced.map((s) => ({ id: s.slug, label: s.name })),
    ...(quoteOptions.length > 0 ? [{ id: QUOTE_TAB, label: 'Grafika' }] : []),
  ]
  const [tab, setTab] = useState(tabs[0]?.id ?? QUOTE_TAB)
  const service = priced.find((s) => s.slug === tab)

  // THE WHOLE PACKAGED PAGE IS THE TILT HOST, as on `/pl/montaz`: the row of
  // levels leans toward the pointer from anywhere on the screen, not only
  // while the cursor is over the cards. The quote half gets a plain box — it
  // has nothing wearing `TILT_GROUP_ITEM`, and a pointer listener that moves
  // nothing is a listener worth not mounting.
  const Shell = sells === 'packages' ? TiltGroup : 'div'

  return (
    <Shell className="relative w-full overflow-x-hidden">
      {/* ONE SCREEN, both halves. The priced one is head, switch and three
          levels; the quote one is head, notice, nine tiles and the summary
          panel that carries the only button on the page. See `lib/tier-fit.ts`
          for the technique and for what gives way on a short window.
          The quote half used to keep the ordinary page rhythm on the grounds
          that a grid scrolls fine. It does — but the panel it scrolls to is
          where the reader says what they need, and the owner's call was that
          nothing on this page should need scrolling to be read or clicked. */}
      <div className={cn(CONTAINER, SCREEN_TOP, OFFER_SCREEN, 'relative z-10')}>
        {/* NO HEADLINE ON `/pl/miniaturki`, the same call the owner made for
            `/pl/montaz`: the page opens on the switch and the three levels.
            `/pl/grafika` sells by enquiry and has no ladder to open on, so it
            keeps the drawn headline — this is the one place the two halves
            of this file part company on the page's first screen. */}
        {sells === 'packages' ? (
          <h1 className="sr-only">Miniaturki — cennik</h1>
        ) : (
          <PageHeader fit>
            Kadr, który <span className={ACCENT_TEXT}>zatrzymuje kciuk.</span>
          </PageHeader>
        )}

        {/* ── TAB SWITCH ─────────────────────────────────────── */}
        {/* SWITCH, LEVELS AND THE RESTOCK FORM AS ONE CENTRED BLOCK on the
            packaged half, as on `/pl/montaz`: the switch sits right above the
            cards and the form right under them, rather than the switch pinned
            to the header and the form to the foot of the screen. The quote
            half keeps the page's ordinary flow — it has tiles, not a ladder. */}
        <div className={cn(sells === 'packages' && 'flex flex-col md:flex-1 md:justify-center')}>
          {tabs.length > 1 ? (
            <div
              role="tablist"
              aria-label="Rodzaj grafiki"
              className={cn(
                RADIUS.strip,
                'mt-[clamp(1rem,3svh,2.5rem)] inline-flex flex-wrap gap-1.5 border border-white/[0.12] bg-white/[0.04] p-1.5 backdrop-blur-md',
                // Centred by its own margins: the pill takes its width from its
                // tabs, so it cannot be centred by a text alignment on the row.
                sells === 'packages' && 'mx-auto justify-center',
              )}
            >
              {tabs.map((t) => {
                const isOn = t.id === tab
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={isOn}
                    onClick={() => setTab(t.id)}
                    className={cn(
                      'px-5 py-2.5 text-[0.95rem] font-semibold transition',
                      RADIUS.stripTab,
                      isOn ? 'bg-white text-neutral-900' : 'text-white/65 hover:text-white',
                    )}
                  >
                    {t.label}
                  </button>
                )
              })}
            </div>
          ) : null}

          {service ? (
            <PricedTab key={service.slug} locale={locale} service={service} />
          ) : quoteOptions.length > 0 ? (
            <UtilityTab locale={locale} options={quoteOptions} />
          ) : (
            // An empty catalogue is a database that failed to load or a page
            // nobody has filled in yet. The route to a human still stands.
            <div className="mt-10">
              <p className={cn(BODY, 'text-white/60')}>
                Cennik grafiki jest w tej chwili niedostępny. Napisz do nas, a wycenimy Twoje
                zlecenie indywidualnie.
              </p>
              <Link
                href={`/${locale}/kontakt`}
                className="mt-6 inline-flex rounded-full border border-white/15 px-6 py-3 text-[0.95rem] font-semibold text-white/80 transition hover:border-white/40 hover:text-white"
              >
                Napisz do nas
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* The work, under the price list. Only on `/pl/miniaturki`: `/pl/grafika`
          sells logos, leaflets and presentations, and a wall of YouTube covers
          under THAT list would be showing the wrong portfolio. */}
      {sells === 'packages' ? <ThumbnailGalleryBand /> : null}
    </Shell>
  )
}

/* ─────────────────────────────────────────────────────────── */

function PricedTab({ locale, service }: { locale: string; service: ServiceView }) {
  // The levels with no free day, by slug — what the „powiadom mnie" form
  // subscribes to. `readyFrom === null` is "asked, and there is nothing";
  // `undefined` means nobody asked, which is not the same state.
  const soldOut = service.plans.filter((plan) => plan.readyFrom === null).map((plan) => plan.slug)

  return (
    <>
      {/* Zdanie o usludze zdjete razem z naglowkiem — `/pl/montaz` tez go nie
          drukuje. Kazda karta ma juz wlasna jednolinijkowke („Prosta,
          skuteczna miniaturka na start."), wiec zdanie nad nimi mowilo to samo
          trzeci raz, zanim ktokolwiek zobaczyl cene. */}

      {/* ── TIER CARDS ─────────────────────────────────────────
          Flat, and the ROW leans with the pointer (`TiltGroup`, mounted by the
          view above) — the three levels move as one object. Each card wears
          `GlowFrame`: a light that travels round the frame, grey on the first
          rung and in the brand hues above it, passed from card to card by
          `phase`. The cards' own lean is off (`tilt={false}`) or a card would
          slide inside its frame.

          The fan is gone from here too. It stood on this page alone after
          `/pl/montaz` dropped it, and the owner's call on 16.10 was that the
          two price lists read as one thing — so `lib/tier-fan.ts` lost its last
          caller and went with the redesign's other orphans. Git history has it
          if the arc is ever wanted back.

          The price runs larger than the shared card's default, as on montaż —
          the override on `--tier-price` reaches the card's own variable
          because `[&_article]` outranks the card's single-class one. */}
      <ul
        className={cn(
          'mt-[clamp(0.75rem,2.4svh,1.5rem)] grid grid-cols-1 gap-6 md:grid-cols-3',
          '[&_article]:[--tier-price:min(28cqw,clamp(2.8rem,11svh_-_1.6rem,7.2rem))]',
          TILT_GROUP_ITEM,
        )}
      >
        {service.plans.map((plan, index) => (
          <li key={plan.slug} className={cn('h-full', plan.popular && 'relative z-10')}>
            <GlowFrame neutral={index === 0} phase={index * 2.33}>
              <TierCard
                locale={locale}
                service={service}
                plan={plan}
                level={index + 1}
                levels={service.plans.length}
                tilt={false}
              />
            </GlowFrame>
          </li>
        ))}
      </ul>

      {soldOut.length > 0 ? (
        <RestockAlertForm
          offeringSlug={service.slug}
          planSlugs={soldOut}
          sourcePath={`/${locale}/grafika`}
        />
      ) : null}
    </>
  )
}

/* ─────────────────────────────────────────────────────────── */

function UtilityTab({ locale, options }: { locale: string; options: QuoteOptionView[] }) {
  const [picked, setPicked] = useState<string[]>([])

  const toggle = (slug: string) =>
    setPicked((prev) => (prev.includes(slug) ? prev.filter((x) => x !== slug) : [...prev, slug]))

  // Carried into the enquiry so the team sees the list without asking again.
  const enquiryHref = useMemo(() => {
    const params = new URLSearchParams({ usluga: 'grafika' })
    if (picked.length > 0) params.set('grafiki', picked.join(','))
    return `/${locale}/kontakt?${params}`
  }, [locale, picked])

  return (
    <>
      {/* ── QUOTE-ONLY NOTICE ────────────────────────────────── */}
      <div
        className={cn(
          'relative mt-[clamp(1rem,2.2svh,2rem)] flex items-center gap-3.5 overflow-hidden border border-white/12 bg-white/[0.05] px-5 py-[clamp(0.6rem,1.4svh,1rem)] backdrop-blur-xl',
          RADIUS.feature,
        )}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(60% 140% at 3% 0%, oklch(0.66 0.21 320 / 0.18), transparent 60%)',
          }}
        />
        <span className="relative flex size-9 shrink-0 items-center justify-center rounded-2xl bg-[image:var(--flou-gradient-solid)] text-white shadow-[0_14px_32px_-16px_var(--flou-glow-core)]">
          <Mail size={16} strokeWidth={2.1} aria-hidden="true" />
        </span>
        {/* One line, on the owner's call. It used to carry a second one —
            „Zakres logo różni się od zakresu ulotki, cennik z półki byłby
            zmyślony. Zaznacz, czego potrzebujesz, a odeślemy wycenę." — which
            argued for the first sentence and then explained the interface. The
            grid of pickable cards directly under it is the instruction, and it
            does not need writing down twice. */}
        <span className={cn(BODY, 'relative font-semibold text-white')}>
          Grafiki użytkowe wyceniamy indywidualnie.
        </span>
      </div>

      {/* ── PICKER ───────────────────────────────────────────── */}
      <ul className="mt-[clamp(0.75rem,1.8svh,1.25rem)] grid grid-cols-1 gap-[clamp(0.6rem,1.5svh,1rem)] sm:grid-cols-2 lg:grid-cols-3">
        {options.map((g) => {
          const isOn = picked.includes(g.slug)
          const Icon = quoteIcon(g.slug)
          const tone = quoteTone(g.slug)
          return (
            <li key={g.slug} className="h-full">
              <TiltCard className="h-full" strength={5}>
                <button
                  type="button"
                  onClick={() => toggle(g.slug)}
                  aria-pressed={isOn}
                  className={cn(
                    'group relative flex h-full w-full flex-col gap-[clamp(0.5rem,1.2svh,0.75rem)] overflow-hidden border p-[clamp(0.85rem,1.7svh,1.25rem)] text-left transition-colors duration-300',
                    RADIUS.feature,
                    isOn
                      ? PICKED_RING
                      : 'border-white/12 bg-white/[0.05] hover:border-white/25 hover:bg-white/[0.08]',
                    // The catch-all keeps its dashed edge — it is the one tile
                    // that is not a named product. A ring beats a dash, so the
                    // dash only applies while it is unpicked.
                    g.isCustom && !isOn ? 'border-dashed' : undefined,
                  )}
                >
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background: `radial-gradient(90% 120% at 12% 0%, color-mix(in oklab, ${tone} ${
                        isOn ? 22 : 13
                      }%, transparent), transparent 66%)`,
                    }}
                  />
                  <span
                    aria-hidden="true"
                    className="tilt-sheen pointer-events-none absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-300 [.tilt-card:hover_&]:opacity-100"
                  />

                  <span className="relative flex items-start justify-between gap-3">
                    {/* The icon plate, the label and the blurb are the tile's
                        three fixed heights, and three rows of them is most of
                        this screen — so each is a `clamp()` against `svh` like
                        the card on `/pl/studio`. Full size from ~1000px of
                        window height; the floors hold the label above the
                        blurb and the blurb above the legibility limit. */}
                    <span
                      className="flex size-[clamp(2.25rem,4.4svh,2.75rem)] items-center justify-center rounded-2xl transition-colors duration-300"
                      style={{
                        color: tone,
                        backgroundColor: `color-mix(in oklab, ${tone} ${isOn ? 22 : 14}%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklab, ${tone} ${
                          isOn ? 45 : 26
                        }%, transparent)`,
                      }}
                    >
                      <Icon size={19} strokeWidth={1.9} aria-hidden="true" />
                    </span>

                    {/* The state, as a control-shaped thing in the corner. It
                        used to be a white disc; white is what `PICKED_FILL`
                        replaced everywhere else on the site. */}
                    <span
                      className={cn(
                        'flex size-6 shrink-0 items-center justify-center rounded-full border transition',
                        isOn ? PICKED_FILL : 'border-white/25 group-hover:border-white/45',
                      )}
                    >
                      {isOn ? <Check size={13} strokeWidth={3} aria-hidden="true" /> : null}
                    </span>
                  </span>

                  <span className="relative block text-[length:clamp(0.95rem,2.1svh,1.05rem)] leading-snug font-bold text-white">
                    {g.label}
                  </span>
                  <span className="relative block flex-1 text-[length:clamp(0.85rem,1.85svh,0.95rem)] leading-relaxed text-white/55">
                    {g.blurb}
                  </span>
                </button>
              </TiltCard>
            </li>
          )
        })}
      </ul>

      {/* ── ENQUIRY ──────────────────────────────────────────── */}
      <section
        aria-label="Podsumowanie wyceny"
        className={cn(
          'relative mt-[clamp(1rem,2.2svh,2rem)] flex flex-col gap-5 overflow-hidden border border-white/12 bg-white/[0.05] p-6 backdrop-blur-xl sm:flex-row sm:items-center sm:justify-between sm:p-[clamp(1.25rem,2.6svh,2rem)]',
          RADIUS.feature,
        )}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{ background: CTA_BLOOM }}
        />

        <div className="relative min-w-0">
          <p className={cn(KICKER, 'flex items-center gap-2 text-white/45')}>
            Do wyceny
            {picked.length > 0 ? (
              <span className="inline-flex size-5 items-center justify-center rounded-full bg-[image:var(--flou-gradient-solid)] text-[0.7rem] font-bold text-white tabular-nums">
                {picked.length}
              </span>
            ) : null}
          </p>

          {/* The picks as chips rather than as a `·`-joined sentence. Nine
              possible labels in one line wrapped into a grey paragraph that
              looked like copy; a chip carrying the option's own glyph is
              something you can count at a glance and match to the grid above. */}
          {picked.length === 0 ? (
            <p className="mt-[clamp(0.25rem,0.8svh,0.5rem)] text-[1.05rem] leading-snug font-semibold text-white/50">
              Nic jeszcze nie zaznaczono
            </p>
          ) : (
            <ul className="mt-[clamp(0.35rem,1svh,0.625rem)] flex flex-wrap gap-2">
              {picked.map((slug) => {
                const option = options.find((g) => g.slug === slug)
                if (!option) return null
                const ChipIcon = quoteIcon(slug)
                const tone = quoteTone(slug)
                return (
                  <li
                    key={slug}
                    className={cn(
                      MICRO,
                      RADIUS.pill,
                      'inline-flex items-center gap-1.5 border px-2.5 py-1 font-semibold',
                    )}
                    style={{
                      color: tone,
                      borderColor: `color-mix(in oklab, ${tone} 40%, transparent)`,
                      backgroundColor: `color-mix(in oklab, ${tone} 14%, transparent)`,
                    }}
                  >
                    <ChipIcon size={12} strokeWidth={2.2} aria-hidden="true" />
                    {option.label}
                  </li>
                )
              })}
            </ul>
          )}

          <p className={cn(MICRO, 'mt-[clamp(0.35rem,1.2svh,0.75rem)] text-white/45')}>
            Wycenę odsyłamy mailem w 1–2 dni robocze. Ceny netto, doliczamy VAT.
          </p>
        </div>

        <Link
          href={enquiryHref}
          className={cn(
            'relative inline-flex shrink-0 items-center justify-center gap-2 px-6 py-3 text-center text-[0.95rem] font-semibold transition-transform duration-300',
            RADIUS.pill,
            picked.length > 0
              ? 'bg-[image:var(--flou-gradient-solid)] text-white shadow-[0_18px_44px_-20px_var(--flou-glow-core)] hover:scale-[1.04]'
              : 'border border-white/15 bg-white/[0.06] text-white/55',
          )}
        >
          <Mail size={15} aria-hidden="true" />
          {picked.length > 0 ? 'Poproś o wycenę' : 'Zaznacz, czego potrzebujesz'}
        </Link>
      </section>
    </>
  )
}
